import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Divider,
  Typography,
  Popper
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Papa from 'papaparse';

const ExportModal = ({ 
  open, 
  onClose, 
  onExport,
  selectedAccount,
  onAccountChange,
  selectedMonth,
  onMonthChange,
  monthOptions,
  visibleTabs,
  selectedTab,
  onMonthYearChange,
  cohortAccounts = [],
  usageData
}) => {
  const [exportOption, setExportOption] = React.useState('all');

  // Define the 'All' option
  const allAccountOption = { accountId: 'ALL', accountName: 'All' };
  // Prepend 'All' to accounts
  const accountOptions = [allAccountOption, ...cohortAccounts];

  // Selection logic for Autocomplete
  const handleAccountsChange = (event, newValue, reason) => {
    // Prevent 'All' from being removed if it's the only selected value
    if (selectedAccount.some(acc => acc.accountId === 'ALL') && newValue.every(acc => acc.accountId === 'ALL')) {
      onAccountChange([allAccountOption]);
      return;
    }
    // Prevent 'All' from being deselected if it's the only selected value (fallback)
    if (selectedAccount && selectedAccount.length === 1 && selectedAccount[0].accountId === 'ALL' && (!newValue || newValue.length === 0)) {
      onAccountChange([allAccountOption]);
      return;
    }
    if (!newValue || newValue.length === 0) {
      onAccountChange([allAccountOption]);
      return;
    }
    if (newValue.some(acc => acc.accountId === 'ALL') &&
        !(selectedAccount && selectedAccount.some(acc => acc.accountId === 'ALL'))) {
      onAccountChange([allAccountOption]);
      return;
    }
    if (selectedAccount && selectedAccount.some(acc => acc.accountId === 'ALL') &&
        newValue.some(acc => acc.accountId !== 'ALL')) {
      onAccountChange(newValue.filter(acc => acc.accountId !== 'ALL'));
      return;
    }
    onAccountChange(newValue.filter(acc => acc.accountId !== 'ALL'));
  };

  const handleExportOptionChange = (event) => {
    setExportOption(event.target.value);
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    onMonthChange(newMonth);
    // Find the API value for the selected month
    const selectedOption = monthOptions.find(option => option.display === newMonth);
    if (selectedOption && onMonthYearChange) {
      onMonthYearChange(selectedOption.value);
    }
  };

  const handleExport = () => {
    onExport({
      exportOption,
      account: selectedAccount,
      month: selectedMonth,
      tab: visibleTabs[selectedTab]
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Export Data</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Filters Section */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Autocomplete
              multiple
              options={accountOptions}
              getOptionLabel={(option) => option.accountName}
              value={Array.isArray(selectedAccount) && selectedAccount.length > 0 ? selectedAccount : [allAccountOption]}
              onChange={handleAccountsChange}
              clearIcon={null}
              PopperComponent={(props) => (
                <Popper
                  {...props}
                  style={{ width: 'max-content', minWidth: '200px' }}
                  placement="bottom-start"
                />
              )}
              renderTags={(value, getTagProps) => {
                const isAll = value.length === 1 && value[0].accountId === 'ALL';
                if (isAll) {
                  return [
                    <span key="all-text" style={{ color: '#444', fontSize: 15, marginLeft: 4 }}>
                      All
                    </span>
                  ];
                }
                return value.map((option, index) => (
                  <Chip
                    key={option.accountId}
                    label={option.accountName}
                    sx={{ 
                      background: '#e0e0e0', 
                      fontSize: 13,
                      maxWidth: 200,
                      '& .MuiChip-label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                    {...getTagProps({ index })}
                  />
                ));
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%'
                    }}
                  >
                    {option.accountName}
                  </Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Accounts"
                  sx={{ 
                    minWidth: 200,
                    '& .MuiAutocomplete-input': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
              )}
              sx={{ 
                minWidth: 200,
                '& .MuiAutocomplete-tag': {
                  maxWidth: '200px'
                }
              }}
            />
            <FormControl variant="standard" sx={{ minWidth: 120 }}>
              <InputLabel>Month:</InputLabel>
              <Select 
                value={selectedMonth} 
                onChange={handleMonthChange} 
                sx={{ borderBottom: '1px solid #000' }} 
                disableUnderline
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.display}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Export Options Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Column options</Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={exportOption}
                onChange={handleExportOptionChange}
              >
                <FormControlLabel 
                  value="all" 
                  control={<Radio />} 
                  label="All" 
                />
                {visibleTabs
                  .filter(tab => tab !== 'ALL')
                  .map((tab, index) => (
                    <FormControlLabel 
                      key={tab}
                      value={`tab_${index}`}
                      control={<Radio />} 
                      label={tab.charAt(0).toUpperCase() + tab.slice(1).toLowerCase()}
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleExport}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal; 